import { useMemo } from "react";
import { useParams } from "react-router-dom";

import {
  TagSuggestionItem,
  TagUser,
  TagEntityType,
  entityToTagEntity,
  workspaceMemberToTagUser,
} from "../types";

import { useDebounce } from "./useDebounce";

import { Entity, EntityType } from "@/queries/folder-entities/types";
import { useListFolderEntities } from "@/queries/folder-entities";
import { useGetWorkspaceMembers } from "@/queries/workspace";

interface UseTagSuggestionsOptions {
  query: string;
  enabled?: boolean;
  allowEntityTypeTags?: boolean;
  allowUserTags?: boolean;
  currentFolderId?: number | null;
}

interface UseTagSuggestionsResult {
  items: TagSuggestionItem[];
  isLoading: boolean;
}

const DEFAULT_SEARCHABLE_ENTITY_TYPES = [
  EntityType.PROMPT,
  EntityType.WORKFLOW,
  EntityType.DATASET,
  EntityType.REPORT,
  EntityType.SNIPPET,
  EntityType.INPUT_VARIABLE_SET,
  EntityType.SKILL_COLLECTION,
  EntityType.SMART_TABLE,
  EntityType.TOOL,
  EntityType.AB_TEST,
  EntityType.FOLDER,
];

// Map entity type names (case-insensitive) to EntityType enum values
const ENTITY_TYPE_NAME_MAP: Record<string, EntityType> = {
  prompt: EntityType.PROMPT,
  prompts: EntityType.PROMPT,
  workflow: EntityType.WORKFLOW,
  workflows: EntityType.WORKFLOW,
  dataset: EntityType.DATASET,
  datasets: EntityType.DATASET,
  report: EntityType.REPORT,
  reports: EntityType.REPORT,
  snippet: EntityType.SNIPPET,
  snippets: EntityType.SNIPPET,
  inputvariableset: EntityType.INPUT_VARIABLE_SET,
  inputvariablesets: EntityType.INPUT_VARIABLE_SET,
  folder: EntityType.FOLDER,
  folders: EntityType.FOLDER,
  skillcollection: EntityType.SKILL_COLLECTION,
  skillcollections: EntityType.SKILL_COLLECTION,
  table: EntityType.SMART_TABLE,
  tables: EntityType.SMART_TABLE,
  smarttable: EntityType.SMART_TABLE,
  smarttables: EntityType.SMART_TABLE,
  tool: EntityType.TOOL,
  tools: EntityType.TOOL,
  abtest: EntityType.AB_TEST,
  abtests: EntityType.AB_TEST,
};

// Display names for entity types
const ENTITY_TYPE_DISPLAY_NAMES: Record<EntityType, string> = {
  [EntityType.PROMPT]: "Prompts",
  [EntityType.WORKFLOW]: "Workflows",
  [EntityType.DATASET]: "Datasets",
  [EntityType.REPORT]: "Reports",
  [EntityType.SNIPPET]: "Snippets",
  [EntityType.INPUT_VARIABLE_SET]: "Input Variable Sets",
  [EntityType.FOLDER]: "Folders",
  [EntityType.AB_TEST]: "AB Tests",
  [EntityType.SMART_TABLE]: "Tables",
  [EntityType.SKILL_COLLECTION]: "Skill Collections",
  [EntityType.TOOL]: "Tools",
};

const getEntityTypeFromQuery = (query: string): EntityType | null => {
  const normalizedQuery = query.toLowerCase().trim();
  return ENTITY_TYPE_NAME_MAP[normalizedQuery] || null;
};

const getRecentMixedEntities = (entities: Entity[], limit: number): Entity[] => {
  const byType: Record<string, Entity[]> = {};

  entities.forEach(entity => {
    if (!byType[entity.type]) {
      byType[entity.type] = [];
    }
    byType[entity.type].push(entity);
  });

  Object.keys(byType).forEach(type => {
    byType[type].sort((a, b) => {
      const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return dateB - dateA;
    });
  });

  const types = Object.keys(byType);
  if (types.length === 0) {
    return [];
  }

  const result: Entity[] = [];
  const indices: Record<string, number> = {};
  types.forEach(type => {
    indices[type] = 0;
  });

  let typeIndex = 0;
  while (result.length < limit) {
    const type = types[typeIndex % types.length];
    const typeEntities = byType[type];

    if (indices[type] < typeEntities.length) {
      result.push(typeEntities[indices[type]]);
      indices[type] += 1;
    }

    typeIndex += 1;

    const allExhausted = types.every(t => indices[t] >= byType[t].length);
    if (allExhausted) {
      break;
    }
  }

  return result;
};

export const useTagSuggestions = ({
  query,
  enabled = true,
  allowEntityTypeTags = false,
  allowUserTags = true,
  currentFolderId = null,
}: UseTagSuggestionsOptions): UseTagSuggestionsResult => {
  const { workspaceId } = useParams();

  const { debouncedValue: debouncedQuery, isPending: isPendingDebounce } = useDebounce(query, 250);

  const hasSearchQuery = query.length > 0;

  const { data: membersData, isLoading: membersLoading } = useGetWorkspaceMembers(
    workspaceId || "",
    enabled && !!workspaceId,
  );

  // Match CommandMenu behavior: only flatten when there's a search query
  const { data: entitiesData, isLoading: entitiesLoading } = useListFolderEntities({
    folder_id: currentFolderId ?? null,
    filter_types: DEFAULT_SEARCHABLE_ENTITY_TYPES,
    flatten: !!debouncedQuery,
    search_query: debouncedQuery || undefined,
    enabled: enabled && !!workspaceId,
    keepPreviousData: false,
    include_metadata: true,
  });

  // Show loading when:
  // 1. We have a search query and are either debouncing or fetching
  // 2. We have no query but entities are still loading initially
  // 3. Members are loading initially
  const isLoading =
    (hasSearchQuery && (isPendingDebounce || entitiesLoading)) ||
    (!hasSearchQuery && entitiesLoading && !entitiesData) ||
    (membersLoading && !membersData);

  const items = useMemo(() => {
    const result: TagSuggestionItem[] = [];
    const lowerQuery = query.toLowerCase();
    const matchedEntityType = getEntityTypeFromQuery(query);

    // If query exactly matches an entity type name, add entity type reference first
    // Only show entity type tags if allowEntityTypeTags is true (e.g., in vibe chat)
    if (
      allowEntityTypeTags &&
      matchedEntityType &&
      query.trim().length > 0 &&
      DEFAULT_SEARCHABLE_ENTITY_TYPES.includes(matchedEntityType)
    ) {
      const entityTypeItem: TagEntityType = {
        type: "entityType",
        entityType: matchedEntityType,
        displayName: ENTITY_TYPE_DISPLAY_NAMES[matchedEntityType],
      };
      result.push(entityTypeItem);
    }

    // Add users matching the query (only if allowUserTags is true)
    if (allowUserTags && membersData?.members) {
      const matchingUsers = membersData.members
        .map(workspaceMemberToTagUser)
        .filter((user: TagUser | null): user is TagUser => {
          if (!user) {
            return false;
          }
          if (!query) {
            return true;
          }
          return (
            user.name.toLowerCase().includes(lowerQuery) ||
            user.email.toLowerCase().includes(lowerQuery)
          );
        })
        .slice(0, 5); // Limit users to 5

      result.push(...matchingUsers);
    }

    // Add entities
    if (entitiesData?.entities) {
      const allEntities = entitiesData.entities;

      // When no search query, get a diverse mix of recent entities from different types
      // When searching, show all results so users can navigate through entities with spaces in names
      const entitiesToShow = hasSearchQuery ? allEntities : getRecentMixedEntities(allEntities, 10);

      const matchingEntities = entitiesToShow.map(entityToTagEntity);
      result.push(...matchingEntities);
    }

    return result;
  }, [
    membersData?.members,
    entitiesData?.entities,
    query,
    hasSearchQuery,
    allowEntityTypeTags,
    allowUserTags,
  ]);

  return {
    items,
    isLoading,
  };
};

export default useTagSuggestions;
